import { Fragment, useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { History } from "lucide-react";
import type { HydratedOpportunity } from "@forgepoint/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { ItemTabError } from "@/modules/items/ItemTabError";
import { StandardAgentThreadView } from "@/modules/standardAgent/components/StandardAgentThreadView";
import { ThreadQuoteSheet } from "@/modules/standardAgent/components/ThreadQuoteSheet";
import { useActivityEventsStore } from "@/store/activityEventsStore";
import { OpportunityActivityEntry } from "./OpportunityActivityEntry";
import { OpportunityActivityFilters } from "./OpportunityActivityFilters";
import { OpportunityActivityTurn } from "./OpportunityActivityTurn";
import {
  buildActivityDays,
  isOpportunityEvent,
  type OpportunityActivityFilter,
} from "./opportunityActivityEntries";
import { useOpportunityActivityNames } from "./useOpportunityActivityNames";

/**
 * The opportunity's activity log in the item history's grammar: day dividers,
 * one line per event, repeat quote views and agent turns folded, with a
 * filter row on top.
 * @param opportunity - The opportunity whose activity to show
 */
export const OpportunityActivityLog = ({
  opportunity,
}: {
  opportunity: HydratedOpportunity;
}) => {
  const opportunityId = opportunity.id;
  const fetchActivityEvents = useActivityEventsStore(
    (state) => state.fetchActivityEvents,
  );
  const { isLoading, error } = useActivityEventsStore(
    useShallow((state) => state.getLoadingState("OPPORTUNITY", opportunityId)),
  );
  // Shallow-compared, so the list keeps its identity until an event changes.
  const entityEvents = useActivityEventsStore(
    useShallow((state) =>
      state.getActivityEventsForEntity("OPPORTUNITY", opportunityId),
    ),
  );
  const events = useMemo(
    () => entityEvents.filter(isOpportunityEvent),
    [entityEvents],
  );
  const names = useOpportunityActivityNames(opportunity, events);
  const [filter, setFilter] = useState<OpportunityActivityFilter>("all");
  const [showTeamViews, setShowTeamViews] = useState(true);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [openQuoteId, setOpenQuoteId] = useState<string | null>(null);

  useEffect(() => {
    fetchActivityEvents("OPPORTUNITY", opportunityId).catch(() => undefined);
  }, [opportunityId, fetchActivityEvents]);

  if (isLoading && events.length === 0) {
    return <ActivitySkeleton />;
  }

  if (error && events.length === 0) {
    return (
      <ItemTabError
        message={error}
        onRetry={() =>
          fetchActivityEvents("OPPORTUNITY", opportunityId).catch(
            () => undefined,
          )
        }
      />
    );
  }

  if (events.length === 0) {
    return <ActivityEmpty label="No activity yet" />;
  }

  const dayGroups = buildActivityDays(events, filter, showTeamViews);

  return (
    <div className="flex flex-col gap-3">
      <OpportunityActivityFilters
        filter={filter}
        onFilterChange={setFilter}
        showTeamViews={showTeamViews}
        onShowTeamViewsChange={setShowTeamViews}
      />
      {dayGroups.length === 0 ? (
        <ActivityEmpty label="No matching activity" />
      ) : (
        <div className="flex flex-col gap-1.5">
          {dayGroups.map((group) => (
            <Fragment key={group.key}>
              <DayDivider label={group.label} />
              {group.entries.map((entry) => {
                if (entry.type === "turn") {
                  return (
                    <OpportunityActivityTurn
                      key={entry.key}
                      turn={entry}
                      names={names}
                      onOpenThread={setOpenThreadId}
                      onOpenQuote={setOpenQuoteId}
                    />
                  );
                }
                return (
                  <OpportunityActivityEntry
                    key={entry.key}
                    event={entry.event}
                    viewCount={entry.viewCount}
                    names={names}
                    onOpenThread={setOpenThreadId}
                    onOpenQuote={setOpenQuoteId}
                  />
                );
              })}
            </Fragment>
          ))}
        </div>
      )}
      <StandardAgentThreadView
        threadId={openThreadId}
        isOpen={openThreadId !== null}
        onClose={() => setOpenThreadId(null)}
      />
      <ThreadQuoteSheet
        quoteId={openQuoteId}
        onClose={() => setOpenQuoteId(null)}
      />
    </div>
  );
};

const DayDivider = ({ label }: { label: string }) => (
  <div className="flex items-center gap-3 px-2.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
    {label}
    <span className="h-px flex-1 bg-border" aria-hidden />
  </div>
);

const ActivityEmpty = ({ label }: { label: string }) => (
  <div className="flex flex-col items-center justify-center gap-1.5 p-6 text-center">
    <History className="mb-1.5 h-5 w-5 text-muted-foreground" aria-hidden />
    <p className="text-sm font-medium">{label}</p>
  </div>
);

const ActivitySkeleton = () => (
  <div className="flex flex-col gap-4 py-3">
    {[0, 1, 2].map((group) => (
      <div key={group} className="flex flex-col gap-2 px-2.5">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-2/3" />
      </div>
    ))}
  </div>
);
